import React, { useState, useRef } from "react";
import {
    LottieLoadingIndicator,
    Toast,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalErrorText,
    StandardModalLabel,
    StandardModalSection,
    StandardModalSectionTitle,
    SegmentedButtonGroup,
    portalColors
} from "src/components";
import styled from "styled-components";
import { useCSVReader } from "react-papaparse";

import EmployeeTemplate from "src/template/EmployeeTemplate.csv";
import ClientTemplate from "src/template/ClientTemplate.csv";
import CloseButton from "src/img/employeeList/CloseButton.png";
import { parsePhoneNumber, getCountryCallingCode } from "react-phone-number-input";
import { getExistingEmployeeAndClientEmails } from "./EditUserList";
import pluralize from "pluralize";

const DirtyState = {
    FALSE: 0,
    TRUE: 1,
    UNKNOWN: 2,
};

const UploadModalContent = styled.div`
    display: grid;
    gap: 1rem;
`;

const UploadCompactSection = styled(StandardModalSection)`
    gap: 1.2rem;
`;

const UploadFieldGroup = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.55rem;
`;

const UserTypeControl = styled.div`
    width: min(100%, 21rem);
`;

const CsvControls = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 1.25rem;
    flex-wrap: wrap;

    > * {
        flex: 0 1 auto;
    }
`;

const ClearUpload = styled.span`
    display: inline-block;
    width: 1.25rem;
    height: 1.25rem;
    background: url(${CloseButton}) no-repeat right transparent;
    background-size: 1.25rem;
    position: absolute;
    top: 8px;
    margin-left: 5px;
    cursor: pointer;
    right: 10px;
`;

const FileNamePill = styled.div`
    width: 250px;
    max-width: 100%;
    position: relative;
    margin-top: 0.75rem;
    padding-left: 10px;
    border: 1px solid ${portalColors.inputBorder};
    border-radius: var(--calendar-radius-control, 8px);
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-shadow: ${portalColors.inputShadow};
    font-size: 0.875rem;
    line-height: 2.25rem;
    box-sizing: border-box;
`;

export function getCountryCallingCodeWithPlusSign(countryCode) {
    try {
        return "+" + getCountryCallingCode(countryCode);
    }
    catch (e) {
        console.error(e)
        return "";
    }
}

const UploadUserListModal = (props) => {
    const { CSVReader } = useCSVReader();
    const { showDialog, handleClose, openEditUserListModal, setUploadUsers, isClient, setIsClient, countryCode, token, customNames } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [fileName, setFileName] = useState(null);
    const fileInput = useRef(null);
    const [items, setItems] = useState(null);
    const [selectedUserTypeIsClient, setSelectedUserTypeIsClient] = useState(isClient);

    // Related to file uploads
    const [errorList, setErrorList] = useState([]);

    // Used by CSVReader
    const upload = (results, file) => {
        setErrorList([]);

        const truncatedFilename = truncateFilename(file?.name || "file.csv", 25);
        setFileName(truncatedFilename);

        setItems(results);
    };

    function truncateFilename(filename, maxLength) {
        const parts = filename.split(/\.(?=[^.]+$)/); //added regex to only get the last `dot` in the string.
        const name = parts[0];
        const extension = parts[1];
        
        if (name.length > maxLength) {
            const truncatedName = name.slice(0, maxLength - 3) + "...";
            return truncatedName + "." + extension;
        } else {
            return filename;
        }
    }

    const clearUpload = () => {
        fileInput.current.value = null;
        setFileName(null);
        setItems(null);
        setErrorList([]);
    };

    const finish = () => {
        setErrorList([]);

        if (!fileName) {
            setErrorList((prevErrorList) => [...prevErrorList, "* Please upload a csv file to continue."]);
            return;
        }
        
        const templateCheckerResult = templateChecker(items);
        if (!templateCheckerResult.isValid) {
            const fieldString = templateCheckerResult.headers.length > 1 ? "fields" : "field";
            setErrorList((prevErrorList) => [...prevErrorList, `* Please upload a CSV file that uses the ${selectedUserTypeIsClient ? `${pluralize.singular(customNames.clients)}` : `${pluralize.singular(customNames.employees)}`} List Template. Missing "${templateCheckerResult.headers}" ${fieldString}`]);
            return;
        }

        submitFile(items);
    };

    const templateChecker = (items) => {
        const firstRow = items?.data?.[0];

        if (!firstRow) {
            return { isValid: false, headers: ["CSV is empty"] };
        }

        const csvHeader = Object.keys(firstRow);

        const templateForClient = ["Client First Name", "Last Name", "Email Address"];
        const templateForEmployee = ["Employee First Name", "Last Name", "Email Address"];

        const missingHeaders = findMissingValues(
            selectedUserTypeIsClient ? templateForClient : templateForEmployee,
            csvHeader
        );

        return {
            isValid: missingHeaders.length === 0,
            headers: missingHeaders
        };
    };

    const findMissingValues = (templateArray, uploadedCsvArray) => {
        const loweredCaseArr2 = uploadedCsvArray.map(value => value.toLowerCase()); //lowercase elements of arr2 for case-insensitive lookups
        const arr2Set = new Set(loweredCaseArr2); // Create a Set for faster lookups
        const missingValues = [];
      
        for (const value of templateArray) {
            const lowerValue = value.toLowerCase(); // Convert current value to lowercase
            if (!arr2Set.has(lowerValue)) { // Check if value exists in the Set
                missingValues.push(value);
            }
        }
      
        return missingValues;
    };

    const submitFile = async (items) => {
        
        if (!items?.data) return;
        let inputs = items.data;

        //Required. make sure email is filled
        inputs = inputs.filter(input => {
            const email = input["Email Address"]?.toLowerCase(); // Safely access and lowercase email
            return email && email.length > 0;
        });

        let data = [];
        
        inputs.forEach(element => {
            let display = {};

            for (const key in element) {
                if (key.toLowerCase() === "Employee First Name".toLowerCase()) { //Required. For Employee
                    display["firstName"] = element[key]; 
                }

                if (key.toLowerCase() === "Client First Name".toLowerCase()) { //Required. For Client
                    display["firstName"] = element[key]; 
                }

                if (key.toLowerCase() === "Last Name".toLowerCase()) { //Required
                    display["lastName"] = element[key]; 
                }

                if (key.toLowerCase() === "Email Address".toLowerCase()) { //Required
                    display["email"] = element[key]; 
                }

                if (key.toLowerCase() === "Mobile Number (Optional)".toLowerCase()) {
                    display["phoneNumber"] = element[key]; 
                }

                if (!selectedUserTypeIsClient) {
                    if (key.toLowerCase() === "Job Title (Optional)".toLowerCase()) {
                        display["employmentPosition"] = element[key]; 
                    }

                    if (key.toLowerCase() === "Department/Group (Optional)".toLowerCase()) {
                        display["department"] = element[key]; 
                    }
                } else {
                    if (key.toLowerCase() === "Client Type (Optional)".toLowerCase()) {
                        display["employmentPosition"] = element[key]; 
                    }
                }
            }

            display["newUser"] = true;
            display["isClient"] = selectedUserTypeIsClient;
            display["isDirty"] = DirtyState.TRUE;
            display["invalidData"] = {};
            data.push(display);
        });

        if (data.length === 0) {
            setErrorList((prevErrorList) => [...prevErrorList, "* Csv file is empty."]);
            return;
        }

        setIsLoading(true);
        const axiosResponse2 = await getExistingEmployeeAndClientEmails(token);
        setIsLoading(false);
        const existingUserEmailsResponse = axiosResponse2.data;
        insertDataToForm(data, existingUserEmailsResponse);
        handleClose();
        openEditUserListModal(existingUserEmailsResponse);
    };

    const insertDataToForm = (data, existingEmails) => {
        //manipulate onload data here
        data.forEach(element => {
            const email = element["email"];
            const isExistingEmployee = existingEmails["existingEmployeeEmails"].includes(email);
            const isExistingClient = existingEmails["existingClientEmails"].includes(email);
            const isNewUser = !isExistingEmployee && !isExistingClient;
            if (!isNewUser) { //use phoneNumber from database and not from CSV
                element["phoneNumber"] = getPhoneByMail(existingEmails.allUsersEmailAndPhone, email);
            }

            element["phoneNumber"] = autoAssignCountryCode(countryCode, element["phoneNumber"]);
        });

        setUploadUsers(data);
    };

    function getPhoneByMail(userList, targetEmail) {
        // Check if userList exists and targetEmail is a string
        if (!userList || typeof targetEmail !== "string") {
            return null;
        }
      
        // Loop through each user object in the list
        for (const userObject of userList) {
            // Access the email property within userObject
            const userEmail = userObject.email;

            // Check if emails match (case-insensitive)
            if (userEmail.toLowerCase() === targetEmail.toLowerCase()) {
                return userObject.phoneNumber; // Return phone number if email matches
            }
        }

        return null; // Return null if email not found in any userObject
    }

    function autoAssignCountryCode(defaultCountry, value) {
        if (typeof value === "string") {
            const phoneNumber = parsePhoneNumber(value);
            const countryCode = getCountryCallingCodeWithPlusSign(defaultCountry);

            if (!phoneNumber) {
                return countryCode + value;
            }
        }

        return value;
    }

    const downloadCsvTemplate = (event) => {
        event.preventDefault();
        const fileName = selectedUserTypeIsClient ? `${pluralize.singular(customNames.clients)}Template.csv` : `${pluralize.singular(customNames.employees)}Template.csv"`;

        fetch(selectedUserTypeIsClient ? ClientTemplate : EmployeeTemplate)
            .then(response => response.blob())
            .then(blob => {
                const fileUrl = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = fileUrl;
                a.download = fileName;
                a.click();
            })
            .catch(e => {
                Toast.error("An error occurred when downloading the file.");
                console.error(e);
            });
    };

    const renderErrors = errorList.map((error, index) => (
        <StandardModalErrorText key={index}>
            {error}
        </StandardModalErrorText>
    ));

    const handleRadioOnClick = bool => {
        setSelectedUserTypeIsClient(bool);
        setIsClient(bool);
    };

    return (
        <React.Fragment>
            <StandardModal
                open={showDialog}
                onClose={handleClose}
                widthMode="wide"
                title="Upload User List"
                description={`Upload multiple users with our handy template, available for download below. After uploading the .csv file, you’ll see a populated list of ${pluralize(customNames.clients)} or ${pluralize(customNames.employees)}. Review and edit your list before saving.`}
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton
                            width="140px"
                            onClick={handleClose}
                            className="form-btns disabled"
                        >
                            Cancel
                        </StandardModalActionButton>
                        { isLoading && <LottieLoadingIndicator width="2em" height="2em" containerHeight="2em" margin="0" /> }
                        { !isLoading &&
                            <StandardModalActionButton
                                $variant="primary"
                                width="140px"
                                type="submit"
                                onClick={finish}
                                className="form-btns disabled"
                            >
                                Preview CSV
                            </StandardModalActionButton>
                        }
                    </StandardModalActionGroup>
                )}
            >
                <UploadModalContent>
                    <UploadCompactSection>
                        <StandardModalSectionTitle>Notes</StandardModalSectionTitle>
                        <StandardModalDescription>
                            Each email address and mobile number should be unique. Only 3 essentials are required: first name, last name and email address.
                        </StandardModalDescription>

                        <UploadFieldGroup>
                            <StandardModalSectionTitle>Select User Type</StandardModalSectionTitle>
                            <UserTypeControl>
                                <SegmentedButtonGroup
                                    options={[
                                        { value: "employee", label: pluralize(customNames.employees) },
                                        { value: "client", label: pluralize(customNames.clients) }
                                    ]}
                                    value={selectedUserTypeIsClient ? "client" : "employee"}
                                    onChange={(value) => handleRadioOnClick(value === "client")}
                                    equalWidth
                                />
                            </UserTypeControl>
                        </UploadFieldGroup>

                        <UploadFieldGroup>
                            <StandardModalSectionTitle>CSV File</StandardModalSectionTitle>
                            <CsvControls>
                                <div>
                                    <StandardModalLabel>Upload CSV File</StandardModalLabel>
                                    <div style={{ position: "relative" }}>
                                        <CSVReader
                                            config={{
                                                header: true
                                            }}
                                            onUploadAccepted={(results, file) => {
                                                upload(results, file);
                                            }}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <StandardModalActionButton
                                                    {...getRootProps()}
                                                    $variant="primary"
                                                    width="180px"
                                                    style={{ marginTop: "0.75rem" }}
                                                > Browse File
                                                </StandardModalActionButton>
                                            )}
                                        </CSVReader>
                                    </div>
                                </div>

                                <div>
                                    <StandardModalLabel>{fileName && "File Name"} &nbsp;</StandardModalLabel>
                                    {fileName && <FileNamePill><span>{fileName}</span><ClearUpload onClick={clearUpload}></ClearUpload></FileNamePill>}
                                    {errorList.length > 0 && (
                                        <div style={{ marginTop: !fileName && "1.45rem", fontSize: "0.8750em" }}>
                                            {renderErrors}
                                        </div>
                                    )}
                                </div>
                            </CsvControls>
                        </UploadFieldGroup>

                        <UploadFieldGroup>
                            <StandardModalSectionTitle>Download CSV Template</StandardModalSectionTitle>
                            <StandardModalActionButton
                                $variant="primary"
                                width="245px"
                                onClick={downloadCsvTemplate}
                            >
                                {selectedUserTypeIsClient ? `${pluralize.singular(customNames.clients)} List Template` : `${pluralize.singular(customNames.employees)} List Template`}
                            </StandardModalActionButton>
                        </UploadFieldGroup>
                    </UploadCompactSection>
                </UploadModalContent>
            </StandardModal>
        </React.Fragment>
    );
}; 

export default UploadUserListModal;
